import React, { useState, useEffect } from "react";
import "./OnboardingModal.css";

const OnboardingModal = ({ isVisible, workspaceId, workspaceName, onClose }) => {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [expandedSections, setExpandedSections] = useState({
		profilePictures: false,
		desktopApp: false,
		mobileApp: false
	});

	useEffect(() => {
		if (isVisible && workspaceId) {
			fetchOnboardingDetails();
		}
	}, [isVisible, workspaceId]);

	const fetchOnboardingDetails = async () => {
		setLoading(true);
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspace/${workspaceId}/onboarding-details`
			);
			if (response.ok) {
				const result = await response.json();
				setData(result);
			}
		} catch (error) {
			console.error("Error fetching onboarding details:", error);
		} finally {
			setLoading(false);
		}
	};

	const toggleSection = (section) => {
		setExpandedSections(prev => ({
			...prev,
			[section]: !prev[section]
		}));
	};

	const renderMemberList = (title, users, type) => {
		if (!users || users.length === 0) return null;
		
		return (
			<div className="member-list-section">
				<h5 className={`member-list-title ${type}`}>
					{type === 'completed' ? '✅' : '⏳'} {title} ({users.length})
				</h5>
				<div className="member-grid">
					{users.map((user, index) => (
						<div key={index} className={`member-card ${type}`}>
							<div className="member-name">{user.name}</div>
							<div className="member-email">{user.email}</div>
						</div>
					))}
				</div>
			</div>
		);
	};

	if (!isVisible) return null;

	return (
		<div className="onboarding-modal-overlay" onClick={onClose}>
			<div className="onboarding-modal" onClick={(e) => e.stopPropagation()}>
				<div className="onboarding-modal-header">
					<h3>🎯 Onboarding Progress</h3>
					<div className="workspace-name">{workspaceName}</div>
					<button className="modal-close-btn" onClick={onClose}>
						×
					</button>
				</div>

				<div className="onboarding-modal-content">
					{loading ? (
						<div className="loading-state">
							<div className="spinner"></div>
							<span>Loading details...</span>
						</div>
					) : data ? (
						<>
							<div className="overall-stats">
								<div className="stat-card main-stat">
									<div className="stat-number">{data.overall.percentage}%</div>
									<div className="stat-label">Overall Progress</div>
									<div className="stat-detail">
										{data.overall.completed} / {data.overall.total} parameters completed
									</div>
								</div>
							</div>

							<div className="breakdown-section">
								<h4>📊 Detailed Breakdown</h4>
								<p className="click-instruction">💡 Click on any category below to see member names</p>
								
								<div className="breakdown-grid">
									<div className="progress-item clickable" onClick={() => toggleSection('profilePictures')}>
										<div className="progress-header">
											<span className="progress-icon">🖼️</span>
											<span className="progress-title">Profile Pictures</span>
											<span className="progress-percentage">{data.profilePictures.percentage}%</span>
											<span className={`expand-icon ${expandedSections.profilePictures ? 'expanded' : ''}`}>▼</span>
										</div>
										<div className="progress-bar">
											<div 
												className="progress-fill profile-progress" 
												style={{ width: `${data.profilePictures.percentage}%` }}
											></div>
										</div>
										<div className="progress-stats">
											<span className="completed">✅ {data.profilePictures.completed} uploaded</span>
											<span className="remaining">⏳ {data.profilePictures.remaining} remaining</span>
										</div>
										{expandedSections.profilePictures && (
											<div className="member-details">
												{renderMemberList("Uploaded Profile Picture", data.profilePictures.completedUsers, "completed")}
												{renderMemberList("Not Uploaded", data.profilePictures.notCompletedUsers, "remaining")}
											</div>
										)}
									</div>

									<div className="progress-item clickable" onClick={() => toggleSection('desktopApp')}>
										<div className="progress-header">
											<span className="progress-icon">💻</span>
											<span className="progress-title">Desktop App</span>
											<span className="progress-percentage">{data.desktopApp.percentage}%</span>
											<span className={`expand-icon ${expandedSections.desktopApp ? 'expanded' : ''}`}>▼</span>
										</div>
										<div className="progress-bar">
											<div 
												className="progress-fill desktop-progress" 
												style={{ width: `${data.desktopApp.percentage}%` }}
											></div>
										</div>
										<div className="progress-stats">
											<span className="completed">✅ {data.desktopApp.completed} installed</span>
											<span className="remaining">⏳ {data.desktopApp.remaining} remaining</span>
										</div>
										{expandedSections.desktopApp && (
											<div className="member-details">
												{renderMemberList("Downloaded Desktop App", data.desktopApp.completedUsers, "completed")}
												{renderMemberList("Not Downloaded", data.desktopApp.notCompletedUsers, "remaining")}
											</div>
										)}
									</div>

									<div className="progress-item clickable" onClick={() => toggleSection('mobileApp')}>
										<div className="progress-header">
											<span className="progress-icon">📱</span>
											<span className="progress-title">Mobile App</span>
											<span className="progress-percentage">{data.mobileApp.percentage}%</span>
											<span className={`expand-icon ${expandedSections.mobileApp ? 'expanded' : ''}`}>▼</span>
										</div>
										<div className="progress-bar">
											<div 
												className="progress-fill mobile-progress" 
												style={{ width: `${data.mobileApp.percentage}%` }}
											></div>
										</div>
										<div className="progress-stats">
											<span className="completed">✅ {data.mobileApp.completed} installed</span>
											<span className="remaining">⏳ {data.mobileApp.remaining} remaining</span>
										</div>
										{expandedSections.mobileApp && (
											<div className="member-details">
												{renderMemberList("Downloaded Mobile App", data.mobileApp.completedUsers, "completed")}
												{renderMemberList("Not Downloaded", data.mobileApp.notCompletedUsers, "remaining")}
											</div>
										)}
									</div>
								</div>
							</div>

							<div className="insights-section">
								<h4>💡 Quick Insights</h4>
								<div className="insights">
									{data.profilePictures.percentage >= 50 && (
										<div className="insight success">
											<span className="insight-icon">🎉</span>
											Profile pictures are doing great! {data.profilePictures.percentage}% completion
										</div>
									)}
									{data.desktopApp.percentage < 30 && (
										<div className="insight warning">
											<span className="insight-icon">📈</span>
											Desktop app adoption needs attention - only {data.desktopApp.percentage}%
										</div>
									)}
									{data.mobileApp.percentage < 25 && (
										<div className="insight warning">
											<span className="insight-icon">📱</span>
											Mobile app has the biggest opportunity - only {data.mobileApp.percentage}%
										</div>
									)}
								</div>
							</div>
						</>
					) : (
						<div className="error-state">
							<span>Failed to load onboarding details</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default OnboardingModal;
